import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { Select } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import classnames from "classnames";
import styled from "styled-components";
import { stripSubgroupFromTitle } from "utils/column/getColumnLabelWithContext";

import { useLengthConversion } from "hooks/useUnitConversion";

import { IGroupBy, IGroupByListColumn } from "models";

import useFilteredGeoCalculationsJobs from "components/org-settings/hooks/useFilteredGeoCalculationsJobs";
import { useGeoSticksCalculationJobs } from "components/org-settings/hooks/useGeoSticksCalculationJobs";
import { StickCalculationsJob } from "components/org-settings/models/StickCalculations";
import useGetScenarioList from "components/pad-generator/hooks/useScenarioList";

import { PresetDropdownContext } from "../filter/focus/PresetDropdown";
import FieldGroup from "./FieldGroup";
import { groupFields } from "./helpers";
import { useSelectedCategory } from "./hooks";
import useOrgGeoSelector from "./hooks/useOrgGeoSelector";

interface OrgGeoSimsCategoryProps {
  toolbarActive?: boolean;
  toolbarFocusedIndex?: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelectRef?: (index: number, selectEl: any | null) => void;
  onFieldClick?: (fieldData: FieldSelectionData) => void;
  onFieldsAvailable?: (fieldsCount: number) => void;
  onFieldItemRef?: (index: number, el: HTMLElement | null) => void;
  active?: boolean;
  focusedIndex?: number | null;
}

interface FieldSelectionData {
  field: IGroupByListColumn;
  onChange?: (
    value: IGroupBy | IGroupByListColumn | Record<string, unknown>,
    isForecastToggleOn?: boolean,
    selectedForecastFolder?: string,
    selectedForecastFolderName?: string,
    selectedPdenSource?: number
  ) => void;
  isForecastToggleOn?: boolean;
  selectedForecastFolder?: string;
  selectedForecastFolderName?: string;
  selectedPdenSource?: number;
}

function OrgGeoSimsCategory({
  toolbarActive = false,
  toolbarFocusedIndex = null,
  onSelectRef,
  onFieldClick,
  onFieldsAvailable,
  onFieldItemRef,
  active = false,
  focusedIndex = null
}: OrgGeoSimsCategoryProps = {}) {
  const shortcutsEnabled = useContext(PresetDropdownContext);

  const [selectedJob, setSelectedJob] = useState<string | undefined>(undefined);
  const { data: projectScenarios } = useGetScenarioList();
  const scenarioIds = useMemo(
    () => projectScenarios?.map((s) => s._id.$oid),
    [projectScenarios]
  );
  const { data: jobs } = useGeoSticksCalculationJobs(scenarioIds);

  const filteredJobs = useFilteredGeoCalculationsJobs(jobs);

  const selectedCat = useSelectedCategory();

  const orgGeoColumns = useOrgGeoSelector(selectedJob);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef = useRef<any | null>(null);

  const { convert, getDisplayUnit } = useLengthConversion();
  const displayUnit = getDisplayUnit();

  const groupedColumns = useMemo(() => groupFields(orgGeoColumns), [orgGeoColumns]);

  const groupedFields = useMemo(
    () =>
      Array.from(groupedColumns).reduce((acc, [key, list]) => {
        // Ungated (no isOrgGeoProperty check): orgGeoColumns comes from
        // useOrgGeoSelector, so every field here is already org-geo — see
        // isOrgGeoProperty's docstring.
        const fields = list
          .map((field) => ({
            ...field,
            title: stripSubgroupFromTitle(field.title, field.subgroup)
          }))
          .sort((a, b) => a.withinSubgroupOrder - b.withinSubgroupOrder);
        if (fields.length > 0) {
          acc.push({ key, fields });
        }
        return acc;
      }, [] as Array<{ key: string; fields: IGroupByListColumn[] }>),
    [groupedColumns]
  );

  // Report select ref to parent when it changes
  useEffect(() => {
    if (onSelectRef) {
      onSelectRef(0, selectRef.current);
    }
  }, [onSelectRef, selectRef.current]);

  // Report field availability to parent when it changes
  useEffect(() => {
    if (onFieldsAvailable) {
      const fieldsCount =
        groupedFields && groupedFields.length > 0
          ? groupedFields.reduce((count, group) => count + group.fields.length, 0)
          : 0;
      onFieldsAvailable(fieldsCount);
    }
  }, [onFieldsAvailable, groupedFields]);

  const changeCalcJob = () => (value) => {
    setSelectedJob(value);
    // Blur the select after selection
    if (selectRef.current) {
      selectRef.current.blur();
    }
  };

  // width based off of (--field-col-width - --space-4*2) / number of columns => (300-(16*2))/4 = 67
  const xdaColumns: ColumnsType<StickCalculationsJob> = [
    {
      title: `XDA Left (${displayUnit})`,
      dataIndex: ["xdaDimensions", "leftWidth"],
      key: "xdaDimensions.leftWidth",
      width: 67,
      render: (value) => (value != null ? convert(value, 2) : value)
    },
    {
      title: `XDA Right (${displayUnit})`,
      dataIndex: ["xdaDimensions", "rightWidth"],
      key: "xdaDimensions.rightWidth",
      width: 67,
      render: (value) => (value != null ? convert(value, 2) : value)
    },
    {
      title: `XDA Up (${displayUnit})`,
      dataIndex: ["xdaDimensions", "upHeight"],
      key: "xdaDimensions.upHeight",
      width: 67,
      render: (value) => (value != null ? convert(value, 2) : value)
    },
    {
      title: `XDA Down (${displayUnit})`,
      dataIndex: ["xdaDimensions", "downHeight"],
      key: "xdaDimensions.downHeight",
      width: 67,
      render: (value) => (value != null ? convert(value, 2) : value)
    }
  ];

  const threedvColumns: ColumnsType<StickCalculationsJob> = [
    {
      title: `3DV Left (${displayUnit})`,
      dataIndex: ["threeDvDimensions", "leftWidth"],
      key: "threeDvDimensions.leftWidth",
      width: 67,
      render: (value) => (value != null ? convert(value, 2) : value)
    },
    {
      title: `3DV Right (${displayUnit})`,
      dataIndex: ["threeDvDimensions", "rightWidth"],
      key: "threeDvDimensions.rightWidth",
      width: 67,
      render: (value) => (value != null ? convert(value, 2) : value)
    },
    {
      title: `3DV Up (${displayUnit})`,
      dataIndex: ["threeDvDimensions", "upHeight"],
      key: "threeDvDimensions.upHeight",
      width: 67,
      render: (value) => (value != null ? convert(value, 2) : value)
    },
    {
      title: `3DV Down (${displayUnit})`,
      dataIndex: ["threeDvDimensions", "downHeight"],
      key: "threeDvDimensions.downHeight",
      width: 67,
      render: (value) => (value != null ? convert(value, 2) : value)
    }
  ];

  if (!selectedCat?.isOrgGeoSimsDefinedColumn || !filteredJobs) return null;
  const jobsOptions = filteredJobs?.length > 0 ? filteredJobs : [];

  const isFieldActive = (fieldStartIndex: number, fieldsLength: number) => {
    if (!focusedIndex) return null;

    if (
      focusedIndex >= fieldStartIndex &&
      focusedIndex < fieldStartIndex + fieldsLength
    ) {
      return focusedIndex - fieldStartIndex;
    }
    return null;
  };

  return (
    <OrgGeoCategoryWrapper>
      <WrapperRow>
        <div>
          <Wrapper>Name</Wrapper>
          <Wrapper>
            <Select
              ref={(el) => {
                selectRef.current = el;
              }}
              className={classnames("toolbar-item", {
                "keyboard-focused": toolbarActive && toolbarFocusedIndex === 0
              })}
              data-testid="geo-calc-job"
              value={selectedJob}
              onChange={changeCalcJob()}
              options={jobsOptions.map((job) => ({ value: job.name, label: job.name }))}
              popupClassName="modal-select"
            />
          </Wrapper>
        </div>
        {selectedJob && (
          <div>
            <TableWrapperRow>
              <TableWrapper>
                <Table
                  data-testid="geo-calc-xda-table"
                  dataSource={jobsOptions.filter((j) => j.name === selectedJob)}
                  columns={xdaColumns}
                  rowKey="jobId"
                  pagination={false}
                  size="small"
                  bordered={true}
                />
              </TableWrapper>
            </TableWrapperRow>
            <TableWrapperRow>
              <TableWrapper>
                <Table
                  data-testid="geo-calc-3dv-table"
                  dataSource={jobsOptions.filter((j) => j.name === selectedJob)}
                  columns={threedvColumns}
                  rowKey="jobId"
                  pagination={false}
                  size="small"
                  bordered={true}
                />
              </TableWrapper>
            </TableWrapperRow>
          </div>
        )}
      </WrapperRow>
      {groupedFields && groupedFields.length > 0 && (
        <FieldListWrapper>
          {groupedFields.map((group, groupIndex) => {
            // Calculate the starting index for this group
            const fieldStartIndex = groupedFields
              .slice(0, groupIndex)
              .reduce((acc, prevGroup) => acc + prevGroup.fields.length, 0);

            return (
              <FieldGroup
                key={group.key}
                groupName={group.key}
                fields={group.fields}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                groups={groupedFields as any}
                dragHandleProps={null}
                isDragging={false}
                shortcutsEnabled={shortcutsEnabled}
                onFieldClick={onFieldClick}
                onItemRef={
                  onFieldItemRef
                    ? (relativeIndex: number, el: HTMLElement | null) => {
                        onFieldItemRef(fieldStartIndex + relativeIndex, el);
                      }
                    : undefined
                }
                active={active}
                focusedIndex={isFieldActive(fieldStartIndex, group.fields.length)}
              />
            );
          })}
        </FieldListWrapper>
      )}
    </OrgGeoCategoryWrapper>
  );
}

export default OrgGeoSimsCategory;

const OrgGeoCategoryWrapper = styled.div`
  display: flex;
  max-height: 100%;
  flex-direction: column;
`;

const FieldListWrapper = styled.div`
  background-color: var(--color-white);
  padding-top: 5px;
  overflow: auto;
  flex-grow: 1;
`;

const Wrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr max-content;
  align-content: center;
  padding: 0 var(--space-2);
`;

const WrapperRow = styled.div`
  display: flex;
  padding: 0 var(--space-2) 5px var(--space-2);
  gap: 10px;
  flex-direction: column;
`;

const TableWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr max-content;
  align-content: center;
  padding: 0 var(--space-2);
`;

const TableWrapperRow = styled.div`
  display: flex;
  flex-direction: column;
`;
